import type { Contact } from "../types";
import type { ContactPatch, SwipeAction, SwipeTone } from "./types";

/**
 * The presentational half of an action.
 *
 * Every field is optional because these builders are used two ways: as a
 * scenario's own action, where the label and tone matter, and as a part inside
 * `combine`, where only the patch is taken and passing a label would be noise.
 * `scenarios.test.ts` asserts that every action a scenario actually offers has
 * a real label, which is where the missing type pressure comes back.
 */
interface ActionMeta {
  label?: string;
  hint?: string;
  tone?: SwipeTone;
  /** Defaults to something derived from the builder; set it to keep a URL or a test stable. */
  id?: string;
}

const present = (meta: ActionMeta) => ({
  label: meta.label ?? "",
  hint: meta.hint,
  tone: meta.tone ?? "neutral",
});

const MS_PER_DAY = 86_400_000;

const addDays = (now: Date, days: number): string =>
  new Date(now.getTime() + days * MS_PER_DAY).toISOString();

/**
 * Write one column. The categorisation scenarios are all built from this.
 *
 * The value goes to the database verbatim, so for a vocabulary-governed column
 * it must be a catalog *value*, not its label — `scenarios.test.ts` checks
 * exactly that, because the alternative is a save error on a live contact
 * after the card has already animated away.
 */
export const setField = (
  field: keyof Contact & string,
  value: string,
  meta: ActionMeta,
): SwipeAction => ({
  id: meta.id ?? `${field}:${value}`,
  ...present(meta),
  patch: () => ({ [field]: value }),
});

/**
 * Add a value to an array column, keeping what is already there.
 *
 * `relationship_types` answers "what roles does this play", and the honest
 * answer is often several — a delivery partner who is also a referral source.
 * So this appends rather than replaces, and a second pass over the deck adds a
 * second role instead of overwriting the first. Undo still works: it snapshots
 * the whole prior array, not the element.
 *
 * `values: []` is how a scenario says "asked, and the answer is no role at
 * all", which clears the column to the empty array rather than to null.
 */
export const setArrayField = (
  field: keyof Contact & string,
  values: readonly string[],
  meta: ActionMeta,
): SwipeAction => ({
  id: meta.id ?? `${field}:${values.join("+") || "none"}`,
  ...present(meta),
  patch: (contact) => {
    if (values.length === 0) return { [field]: [] };
    const existing = (contact[field] as string[] | null | undefined) ?? [];
    return { [field]: Array.from(new Set([...existing, ...values])) };
  },
});

/**
 * Push the next touch out by N days and mark it scheduled.
 *
 * "Not now" is a real answer to a follow-up queue, and the queue only stops
 * re-serving the contact if the deferral is written down.
 */
export const snoozeFor = (days: number, meta: ActionMeta): SwipeAction => ({
  id: meta.id ?? `snooze:${days}`,
  ...present(meta),
  patch: (_contact, now) => ({
    next_touch_date: addDays(now, days),
    next_touch_status: "Scheduled",
  }),
});

/**
 * Record that a real interaction happened, and close out the plan that led to
 * it. This is the one action that moves `last_meaningful_interaction_at`, the
 * field every staleness calculation in the CRM hangs off.
 */
export const markTouched = (meta: ActionMeta): SwipeAction => ({
  id: meta.id ?? "touched",
  ...present(meta),
  patch: (_contact, now) => ({
    last_meaningful_interaction_at: now.toISOString(),
    next_touch_status: "Done",
    next_touch_date: null,
  }),
});

/** Drop the follow-up plan without claiming anything happened. */
export const clearFollowUp = (meta: ActionMeta): SwipeAction => ({
  id: meta.id ?? "clear-follow-up",
  ...present(meta),
  patch: () => ({ next_touch_date: null, next_touch_status: "No Plan" }),
});

/**
 * One answer that writes several columns, built from the actions above.
 *
 * Later patches win on a shared key, so the order reads as "…and also".
 * Used where a decision is genuinely compound: choosing to restart a dormant
 * relationship both picks the play and puts a date on it, and writing one
 * without the other leaves the record lying about its own state.
 */
export const combine = (
  meta: ActionMeta & { id: string },
  ...actions: SwipeAction[]
): SwipeAction => ({
  id: meta.id,
  ...present(meta),
  patch: (contact, now) =>
    actions.reduce<ContactPatch>(
      (patch, action) => ({ ...patch, ...action.patch(contact, now) }),
      {},
    ),
});

/**
 * A fixed instant for probing what an action writes. The KEYS and literal
 * values of a patch never depend on the clock; only dates do.
 */
const PROBE = new Date(0);

/**
 * Does this action write the given value anywhere in its patch?
 *
 * Used to ring the answer a first-pass recommendation points at. Checks every
 * field rather than the first, because a composed action writes several and
 * the recommended value is not necessarily in the one that happens to be
 * declared first.
 */
export const actionWrites = (
  action: SwipeAction,
  value: string | null | undefined,
  contact: Contact,
): boolean => {
  if (value == null) return false;
  // An array column holds the recommended value as a MEMBER, not as the whole
  // value: a suggestion of 'audience owner' has to ring an action that writes
  // ['audience owner', 'client'].
  return Object.values(action.patch(contact, PROBE)).some((v) =>
    Array.isArray(v) ? v.includes(value) : v === value,
  );
};
